import React, { useMemo } from "react";
import { RouteComponentProps, useHistory } from "react-router";
import { TaskFilter } from "../../../common/service/server/controller/TaskController";
import { TaskDto } from "../../../common/service/server/dto/TaskDto";
import { PageHeader } from "../../components/layout/header/Header";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";
import { EmptyTodoTasks, TaskCard } from "../../components/organisms/tasks/TaskCard";
import { DPATaskCard } from "../../components/organisms/tasks/DPATaskCard";
import { useOrganization, usePrimaryOrganizationId } from "../../hooks";
import { useTasks } from "../../hooks/useTasks";
import { completedTaskComparitor, todoTaskComparitor } from "../../util/tasks";
import { TabsWithUnderline } from "@truevault/aquilo";

const filterForTab = (tab: TasksTab): TaskFilter => {
  if (tab == "open") {
    return "active";
  }
  if (tab == "completed") {
    return "complete";
  }
  return "active";
};

type TasksTab = "open" | "completed";
type Props = {
  filter: TasksTab;
};

type PageProps = RouteComponentProps & Props;

export const Listing = ({ filter: defaultTab }: PageProps) => {
  const primaryOrgId = usePrimaryOrganizationId();
  const [, , refreshOrg] = useOrganization(primaryOrgId);
  const history = useHistory();

  // Replace useTabs with direct tab management
  const tabs = [
    { name: "Open", value: "open" },
    { name: "Completed", value: "completed" },
  ];

  const handleTabChange = (newVal: TasksTab) => {
    const url = new URL(location.toString());
    url.pathname = url.pathname.split("/").slice(0, -1).concat(newVal).join("/");
    history.push(url.href.replace(url.origin, ""));
    refreshOrg();
  };

  const tabsConfig = tabs.map((tab) => ({
    name: tab.name,
    current: tab.value === defaultTab,
    onClick: () => handleTabChange(tab.value as TasksTab),
  }));

  const filter = filterForTab(defaultTab);
  const [tasks, tasksRequest, _refreshTasks, handleComplete] = useTasks(primaryOrgId, filter);

  return (
    <StandardPageWrapper requests={[tasksRequest]}>
      <PageHeader titleContent="To-Do List" />

      <div className="mt-md mb-lg tw-boundary">
        <TabsWithUnderline tabs={tabsConfig} />
      </div>

      {filter == "active" && <TodoTasks tasks={tasks} onComplete={handleComplete} />}
      {filter == "complete" && <CompletedTasks tasks={tasks} />}
    </StandardPageWrapper>
  );
};

type TodoTaskProps = {
  tasks: TaskDto[];
  onComplete: (task: TaskDto) => Promise<void>;
};

const TodoTasks: React.FC<TodoTaskProps> = ({ tasks, onComplete }) => {
  const sortedTasks = useMemo(() => tasks?.sort(todoTaskComparitor), [tasks]);

  // Separate DPA tasks from other tasks - get ALL DPA tasks (completed and non-completed)
  const { allDpaTasks, notDpaTasks } = useMemo(() => {
    const allDpaTasks = sortedTasks?.filter((task) => task.taskKey.startsWith("DPA_")) || [];
    const notDpaTasks = sortedTasks?.filter((task) => !task.taskKey.startsWith("DPA_")) || [];
    return { allDpaTasks, notDpaTasks };
  }, [sortedTasks]);

  if (!tasks?.length) {
    return <EmptyTodoTasks />;
  }

  return (
    <>
      {/* Regular (non-DPA) tasks */}
      {notDpaTasks.map((t) => (
        <div key={t.id}>
          <TaskCard task={t} onComplete={onComplete} />
        </div>
      ))}

      {/* DPA tasks with special styling/handling - show ALL DPA tasks */}
      {allDpaTasks.length > 0 && (
        <div>
          <DPATaskCard tasks={allDpaTasks} />
        </div>
      )}
    </>
  );
};

type CompletedTasksProps = {
  tasks: TaskDto[];
};

const CompletedTasks: React.FC<CompletedTasksProps> = ({ tasks }) => {
  const sortedTasks = useMemo(() => tasks?.sort(completedTaskComparitor), [tasks]);

  // Separate DPA tasks from other tasks for completed tasks too
  const { dpaTasks, notDpaTasks } = useMemo(() => {
    const dpaTasks = sortedTasks?.filter((task) => task.taskKey.startsWith("DPA_")) || [];
    const notDpaTasks = sortedTasks?.filter((task) => !task.taskKey.startsWith("DPA_")) || [];
    return { dpaTasks, notDpaTasks };
  }, [sortedTasks]);

  if (!tasks?.length) {
    return <p className="empty-state-message">You have no completed to-do items.</p>;
  }

  return (
    <div>
      {/* Regular (non-DPA) completed tasks */}
      {notDpaTasks.map((t) => (
        <div key={t.id}>
          <TaskCard task={t} allowComplete={false} completed={true} />
        </div>
      ))}

      {/* Completed DPA tasks with special styling/handling */}
      {dpaTasks.length > 0 && (
        <div>
          <DPATaskCard tasks={dpaTasks} completed={true} />
        </div>
      )}
    </div>
  );
};
